import React, { useEffect, useState, useRef } from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
  Flex,
} from "@chakra-ui/react";
import { useSession } from "next-auth/client";
import * as analytics from "utils/analytics";
import { useRouter } from 'next/router';
import { getAccount } from "utils/sessionHelper";
/**
 * Component that handles Twitter authorization flow
 * Displays a message and button to initiate Twitter OAuth process
 */
export function TwitterScopeValidator() {
  const [session] = useSession();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const extraAuthRef = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const router = useRouter();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    async function checkScopes() {
      if (hasCheckedRef.current) {
        return;
      }

      if (router.pathname.startsWith('/sign')) {
        return;
      }

      if (session?.user?.uid) {
        try {
          const account = getAccount(session);
          const isMainAccount = account.id === account.idAccount;
          if (isMainAccount) {
            const response = await fetch(`/api/auth/check-scopes?userId=${account.id}`);
            const data = await response.json();

            if (response.ok && !data.hasRequiredScopes) {
              setTimeout(() => {
                setIsOpen(true);
              }, 2000);
            } else {
              setIsOpen(false);
            }

            hasCheckedRef.current = true;
          }
        } catch (error) {
          console.error('Error checking scopes:', error);
          setIsOpen(false);
        }
      }
    }

    checkScopes();
  }, [session, router.pathname]);

  const onClose = () => setIsOpen(false);

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={extraAuthRef}
      isCentered
      motionPreset="slideInBottom"
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          textAlign="center"
          mx="auto"
          my="auto"
        >
          <AlertDialogHeader fontSize="xl" fontWeight="bold">
            One more step
          </AlertDialogHeader>
          <AlertDialogBody>
            <Flex flexDirection="column" alignItems="center" gap={6} py={2}>
              <Text fontSize="md">
                To continue, we need a bit of extra authorization from you.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Click the button and let us handle the rest for you.
              </Text>
            </Flex>
          </AlertDialogBody>
          <AlertDialogFooter justifyContent="center">
            <Button
              variant="primary"
              size="md"
              ref={extraAuthRef}
              as="a"
              href={'sign'}
              target="_blank"
              onClick={() => {
                analytics.log("hit_getExtraAuth");
                onClose();
              }}
            >
              Authorize now
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
